const API_PREFIX = '/api';
const PATH_PARAM_SEGMENT = /^\{[^}]+\}$/;

// precompiled regexes for API prefix checks and replacement
const API_PREFIX_TEST_RE = new RegExp(`^${API_PREFIX}(?:/|$)`, 'i');
const API_PREFIX_REPLACE_RE = new RegExp(`^${API_PREFIX}`, 'i');

type ContractRouteDefinition = {
  method?: unknown;
  path?: unknown;
};

export type TrackedContractRoute = {
  key: string;
  method: string;
  path: string;
};

const splitSegments = (path: string): string[] => {
  const trimmed = path.replace(/^\/+|\/+$/g, '');
  return trimmed ? trimmed.split('/') : [];
};

const toRequestPath = (url: string): string => {
  if (!url) {
    return '';
  }

  if (url.startsWith('/')) {
    return url;
  }

  try {
    return new URL(url).pathname;
  } catch {
    const hasWindow = typeof window !== 'undefined';
    const origin = hasWindow && window.location?.origin ? window.location.origin : undefined;
    if (origin) {
      try {
        return new URL(url, origin).pathname;
      } catch {
        return url;
      }
    }
    return url;
  }
};

const stripApiPrefix = (requestPath: string): string | undefined => {
  if (!requestPath) {
    return undefined;
  }

  if (!API_PREFIX_TEST_RE.test(requestPath)) {
    return undefined;
  }

  const withoutPrefix = requestPath.replace(API_PREFIX_REPLACE_RE, '');
  return withoutPrefix || '/';
};

const isPathSegmentMatch = (contractSegment: string, requestSegment: string): boolean =>
  PATH_PARAM_SEGMENT.test(contractSegment) || contractSegment.toLowerCase() === requestSegment.toLowerCase();

const isContractPathMatch = (contractPath: string, requestPath: string): boolean => {
  const pathWithoutApiPrefix = stripApiPrefix(requestPath);
  if (pathWithoutApiPrefix === undefined) {
    return false;
  }

  const contractSegments = splitSegments(contractPath);
  const requestSegments = splitSegments(pathWithoutApiPrefix);

  // Exact segment count: all literal segments must match and {param} can match anything.
  if (contractSegments.length === requestSegments.length) {
    return contractSegments.every((segment, index) => isPathSegmentMatch(segment, requestSegments[index] ?? ''));
  }

  // Allow optional last param segment (e.g. /models/configuration/{version}) to also
  // match collection path /api/models/configuration.
  const hasOptionalTrailingParam =
    contractSegments.length === requestSegments.length + 1 &&
    PATH_PARAM_SEGMENT.test(contractSegments[contractSegments.length - 1] ?? '');

  if (!hasOptionalTrailingParam) {
    return false;
  }

  return contractSegments.slice(0, -1)
    .every((segment, index) => isPathSegmentMatch(segment, requestSegments[index] ?? ''));
};

export const getTrackedContractRoutes = (
  routeDefinitions: Record<string, ContractRouteDefinition>,
): TrackedContractRoute[] =>
  Object.entries(routeDefinitions).reduce<TrackedContractRoute[]>((acc, [key, route]) => {
    if (typeof route.method === 'string' && typeof route.path === 'string') {
      acc.push({ key, method: route.method.toUpperCase(), path: route.path });
    }
    return acc;
  }, []);

export const findTrackedContractRoute = (
  url: string,
  method: string,
  trackedRoutes: TrackedContractRoute[],
): { key: string; path: string } | undefined => {
  const requestPath = toRequestPath(url);
  const normalizedMethod = method.toUpperCase();

  const match = trackedRoutes.find((route) =>
    route.method === normalizedMethod && isContractPathMatch(route.path, requestPath));

  return match ? { key: match.key, path: match.path } : undefined;
};
